import ErrorSvg from '@euler/app/flows/error/assets/error.svg';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { getColorTheme } from '@euler/app/theming';
import { ColorModeManager } from '@euler/app/theming/color-mode';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { spxFactory } from '@euler/functions/responsive/utils';
import { FallbackProps } from 'react-error-boundary';
import {
  Dimensions,
  SafeAreaView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

export const ReactErrorFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  return <ErrorFallback error={error} resetError={resetErrorBoundary} />;
};

export const ErrorFallback = (props: {
  error: Error | null | undefined;
  componentStack?: string;
  eventId?: string | null;
  resetError?: () => void;
}) => {
  if (!props.error) {
    return null;
  }

  // Note(eric): it's not safe to use several hooks that depends on
  // some app level context providers in case when the error fallback
  // is rendered at the top level of the application.
  const theme = getColorTheme(ColorModeManager.default.current ?? 'dark');
  const dimens = Dimensions.get('window');
  const size = dimens.width * 1.5;
  const rpx = spxFactory();

  return (
    <SafeAreaView
      css={`
        flex: 1;
        background-color: white;
        justify-content: center;
      `}
    >
      <View
        css={`
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          align-items: center;
          justify-content: center;
        `}
        pointerEvents="none"
      >
        <ErrorSvg
          width={size}
          height={size}
          style={{
            left: dimens.width / 4,
          }}
        />
      </View>
      <TouchableOpacity
        onPress={() => {
          const nav = getGlobalNavigationRef();
          if (nav.isReady() && nav.canGoBack()) {
            nav.goBack();
          }
        }}
        css={`
          position: absolute;
          left: ${rpx(15)}px;
          top: ${rpx(32)}px;
        `}
      >
        <Icon name="arrow-back-circle-outline" size={rpx(44)} color="black" />
      </TouchableOpacity>
      <View
        css={`
          padding: 0 ${rpx(15)}px;
        `}
      >
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Black};
            font-size: ${rpx(52)}px;
            margin-bottom: ${rpx(32)}px;
            font-weight: bold;
          `}
        >
          出错啦!
        </Text>
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Light};
            margin-bottom: ${rpx(5)}px;
            font-size: ${rpx(18)}px;
          `}
        >
          处理你的请求时发生了错误，请稍候重试
        </Text>
        {props.error?.message ? (
          <Text
            css={`
              margin-bottom: ${rpx(10)}px;
              font-family: ${FontFamily.NotoSans.Light};
              color: ${StatusColors.Danger};
            `}
          >
            {props.error?.message}
          </Text>
        ) : null}
        <View
          css={`
            flex-direction: row;
            margin-top: ${rpx(44)}px;
          `}
        >
          <TouchableOpacity
            onPress={props.resetError}
            css={`
              margin: 0;
              background-color: ${theme.link};
              padding: ${rpx(3)}px ${rpx(24)}px;
              height: ${rpx(38)}px;
              border-radius: ${rpx(22)}px;
              justify-content: center;
              align-items: center;
            `}
          >
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(16)}px;
                color: #fff;
              `}
            >
              重试
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};
